import {
  type PaymentCategory,
  workerPaymentsContract,
  type WorkerPaymentsResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { supportQueryKeys } from "./queryKeys";

/**
 * Valid payment status values from the payments API.
 */
const VALID_PAYOUT_STATUSES = ["paid", "pending", "failed"] as const;

/**
 * Payment status values from the payments API.
 * These match the values used in the Earnings page.
 */
export type PayoutStatus = (typeof VALID_PAYOUT_STATUSES)[number];

/**
 * Valid payout method values from the payments API.
 */
const VALID_PAYOUT_METHODS = ["instant", "standard"] as const;

/**
 * Payout method - instant or standard.
 */
export type PayoutMethod = (typeof VALID_PAYOUT_METHODS)[number];

/**
 * Type guard to validate payout status value.
 */
function isValidPayoutStatus(value: unknown): value is PayoutStatus {
  return typeof value === "string" && VALID_PAYOUT_STATUSES.includes(value as PayoutStatus);
}

/**
 * Type guard to validate payout method value.
 */
function isValidPayoutMethod(value: unknown): value is PayoutMethod {
  return typeof value === "string" && VALID_PAYOUT_METHODS.includes(value as PayoutMethod);
}

/**
 * Payment status information for a shift.
 * This matches the data structure used in the Earnings page.
 *
 * IMPORTANT: The payments API does not support filtering by shift ID.
 * We fetch a limited number of recent payments and search for the shift's payment.
 * If the shift's payment is not found in the fetched results, `isUnknown` will be true.
 * This can happen if:
 * - The shift hasn't been verified/paid yet (expected for recent shifts)
 * - The payment exists but is beyond the search limit (older shifts)
 *
 * When `isUnknown` is true, the UI should display an appropriate message
 * rather than assuming any particular status.
 */
export interface ShiftPaymentStatus {
  readonly payoutStatus: PayoutStatus | undefined;
  readonly payoutMethod: PayoutMethod | undefined;
  readonly amountInCents: number | undefined;
  /**
   * ISO datetime string when the payment was created/processed.
   * Used to display "Paid on [date]" for completed payments.
   */
  readonly createdAt: string | undefined;
  /**
   * Payment category from the payments API (e.g., "shift-verification").
   * Used to display category-specific information matching the Earnings page.
   */
  readonly paymentCategory: PaymentCategory | undefined;
  /**
   * If present, this payment is a reversal of the referenced payment.
   * Matches the Earnings page's reversal detection logic.
   */
  readonly reversedPaymentId: string | undefined;
  /**
   * Hours worked from the included shift in the payments API response.
   * This is the source of truth used by the Earnings page.
   */
  readonly includedShiftTimeInHours: number | undefined;
  /**
   * Pay rate in dollars from the included shift in the payments API response.
   * This is the source of truth used by the Earnings page.
   */
  readonly includedShiftPayInDollars: number | undefined;
  /**
   * True when the payment status could not be determined.
   * This happens when the shift's payment record was not found in the
   * fetched payment results. The UI should handle this gracefully
   * (e.g., show "Payment status unavailable" instead of assuming pending).
   */
  readonly isUnknown: boolean;
}

interface UseGetShiftPaymentStatusParams {
  readonly shiftId: string;
  readonly enabled?: boolean;
}

interface UseGetShiftPaymentStatusResult {
  readonly data: ShiftPaymentStatus | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/**
 * Fetches the payment status for a specific shift.
 * Uses the same payments API as the Earnings page to ensure data consistency.
 *
 * LIMITATION: The payments API does not support server-side filtering by shift ID.
 * This hook fetches a limited number of recent payments (100) and searches for
 * the shift's payment client-side. If the payment is not found in this batch,
 * the result will have `isUnknown: true` rather than assuming a particular status.
 *
 * This approach is non-deterministic for workers with many payments, as older
 * shift payments may not appear in the first 100 results. The UI should handle
 * the `isUnknown` case appropriately (e.g., "Payment status unavailable").
 */
export function useGetShiftPaymentStatus(
  params: UseGetShiftPaymentStatusParams
): UseGetShiftPaymentStatusResult {
  const { shiftId, enabled = true } = params;
  const worker = useDefinedWorker();

  const query = useQuery(
    supportQueryKeys.shiftPaymentStatus(worker.userId, shiftId),
    async (): Promise<WorkerPaymentsResponse> => {
      // Fetch payments from the same API as the Earnings page
      const result = await get({
        url: `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/worker-payments`,
        queryParams: {
          // Fetch recent payments - the shift payment should be in the first page
          "page[limit]": "100",
        },
        responseSchema: workerPaymentsContract.list.responses[200],
      });

      return result.data as WorkerPaymentsResponse;
    },
    {
      select: (data: WorkerPaymentsResponse): ShiftPaymentStatus => {
        // Find the payment record for this specific shift
        const payment = data.data.find((item) => item.attributes.shiftId === shiftId);

        // If no payment found, return explicit unknown status.
        // This is non-deterministic: the payment may exist beyond the fetched results.
        // The UI should handle this gracefully rather than assuming any status.
        if (!payment) {
          return {
            payoutStatus: undefined,
            payoutMethod: undefined,
            amountInCents: undefined,
            createdAt: undefined,
            paymentCategory: undefined,
            reversedPaymentId: undefined,
            includedShiftTimeInHours: undefined,
            includedShiftPayInDollars: undefined,
            isUnknown: true,
          };
        }

        const {
          payoutStatus,
          payoutMethod,
          amountInCents,
          createdAt,
          paymentCategory,
          reversedPaymentId,
        } = payment.attributes;

        // Find the included shift data (source of truth for hours/rate, matching Earnings page)
        const includedShift = data.included.find(
          (item) => item.type === "shift" && item.id === shiftId
        );
        const shiftAttributes =
          includedShift?.type === "shift" ? includedShift.attributes : undefined;

        return {
          payoutStatus: isValidPayoutStatus(payoutStatus) ? payoutStatus : undefined,
          payoutMethod: isValidPayoutMethod(payoutMethod) ? payoutMethod : undefined,
          amountInCents,
          createdAt,
          paymentCategory,
          reversedPaymentId,
          includedShiftTimeInHours: shiftAttributes?.time,
          includedShiftPayInDollars: shiftAttributes?.pay,
          isUnknown: false,
        };
      },
      enabled: enabled && Boolean(shiftId),
      staleTime: 30_000, // 30 seconds - keep data fresh
      meta: {
        userErrorMessage: "Failed to load payment status",
      },
    }
  );

  // Track whether we've already logged for this shiftId to avoid duplicate logs
  const hasLoggedUnknownRef = useRef<string | null>(null);

  // Log analytics when payment status is unknown (for observability)
  useEffect(() => {
    if (query.data?.isUnknown && hasLoggedUnknownRef.current !== shiftId) {
      hasLoggedUnknownRef.current = shiftId;
      logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_PAYMENT_STATUS_UNKNOWN, {
        shiftId,
        reason: "payment_not_in_first_100_results",
      });
    }
  }, [query.data?.isUnknown, shiftId]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    refetch: () => {
      void query.refetch();
    },
  };
}
